import { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PublishIcon from '@mui/icons-material/Publish';
import { subscribeSaveStatus } from '../../../services/workspaceRuntime/workspaceDraftService';

const SAVE_STATUS_LABELS = {
  idle:    null,
  pending: { label: 'Unsaved changes', color: 'warning' },
  saving:  { label: 'Saving…',         color: 'default' },
  saved:   { label: 'Saved',           color: 'success' },
  error:   { label: 'Save failed',     color: 'error'   },
};

export default function StudioHeader({
  workspaceName,
  onPublish,
  onExit,
  isPublishing,
  hasDraftVersion,
}) {
  const [saveStatus, setSaveStatus] = useState('idle');

  useEffect(() => {
    let revertTimer = null;
    const unsub = subscribeSaveStatus((status) => {
      setSaveStatus(status);
      if (status === 'saved') {
        clearTimeout(revertTimer);
        revertTimer = setTimeout(() => setSaveStatus('idle'), 3000);
      }
    });
    return () => {
      unsub();
      clearTimeout(revertTimer);
    };
  }, []);

  const statusInfo = SAVE_STATUS_LABELS[saveStatus] ?? null;

  return (
    <AppBar position="static" color="default" elevation={1} sx={{ zIndex: 10 }}>
      <Toolbar variant="dense" sx={{ gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            Home Designer
          </Typography>
          {workspaceName && (
            <>
              <Typography variant="body2" color="text.secondary">
                /
              </Typography>
              <Typography variant="subtitle1" noWrap>
                {workspaceName}
              </Typography>
            </>
          )}
        </Box>

        {statusInfo && (
          <Chip
            size="small"
            label={statusInfo.label}
            color={statusInfo.color}
            variant="outlined"
            icon={statusInfo.color === 'default' ? <CircularProgress size={10} color="inherit" /> : undefined}
          />
        )}

        <Chip
          size="small"
          label="Editing Draft"
          color="warning"
          variant="outlined"
        />

        <Tooltip
          title={
            isPublishing
              ? 'Publishing…'
              : hasDraftVersion
                ? 'Promote current draft to live published version'
                : 'No pending draft – make changes in the canvas first'
          }
        >
          <span>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={isPublishing ? <CircularProgress size={14} color="inherit" /> : <PublishIcon fontSize="small" />}
              onClick={onPublish}
              disabled={isPublishing}
            >
              {isPublishing ? 'Publishing…' : 'Publish'}
            </Button>
          </span>
        </Tooltip>

        <Button
          size="small"
          variant="outlined"
          startIcon={<ExitToAppIcon fontSize="small" />}
          onClick={onExit}
        >
          Exit Designer
        </Button>
      </Toolbar>
    </AppBar>
  );
}
